// Composant Liste des Critères de Scoring Détaillés
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

export interface ScoreCriterion {
  label: string;
  score: number;
  maxScore: number;
  details: string;
}

interface ScoreCriteriaListProps {
  criteria: ScoreCriterion[];
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};

  @media print {
    page-break-inside: avoid;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const Score = styled.div<{ score: number; maxScore: number }>`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${props => {
    const percentage = (props.score / props.maxScore) * 100;
    if (percentage >= 80) return theme.colors.success;
    if (percentage >= 60) return theme.colors.warning;
    return theme.colors.error;
  }};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;

  @media print {
    display: none;
  }
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => {
    if (props.percentage >= 80) return theme.colors.success;
    if (props.percentage >= 60) return theme.colors.warning;
    return theme.colors.error;
  }};
  transition: width 0.3s ease;
`;

const Details = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  padding-left: ${theme.spacing.md};
`;

export const ScoreCriteriaList: React.FC<ScoreCriteriaListProps> = React.memo(({ criteria }) => {
  return (
    <List>
      {criteria.map((criterion, index) => {
        const percentage = (criterion.score / criterion.maxScore) * 100;
        return (
          <Item key={index}>
            <Header>
              <Label>{criterion.label}</Label>
              <Score score={criterion.score} maxScore={criterion.maxScore}>
                {criterion.score}/{criterion.maxScore}
              </Score>
            </Header>
            <ProgressBar>
              <ProgressFill percentage={percentage} />
            </ProgressBar>
            <Details>{criterion.details}</Details>
          </Item>
        );
      })}
    </List>
  );
});
